import { zodResolver } from "@hookform/resolvers/zod";
import { createRoleSchema, type Permission, type Role } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  Drawer,
  FormField,
  Input,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Lock, Plus, ShieldAlert } from "lucide-react";
import { Fragment, type ReactElement, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { api } from "../../../lib/api.js";
import { applyFieldErrors, errorMessage } from "../../../lib/errors.js";
import { useDocumentTitle, useDraftGuard } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";
import { useStepUp } from "../../../lib/step-up.js";

export const Route = createFileRoute("/_app/ayarlar/roller")({ component: RolesPage });

interface CatalogItem {
  code: Permission;
  module: string;
  description: string;
  stepUp: boolean;
}
type ModuleGroup = [module: string, items: CatalogItem[]];

function groupByModule(items: CatalogItem[]): ModuleGroup[] {
  const groups = new Map<string, CatalogItem[]>();
  for (const item of items) groups.set(item.module, [...(groups.get(item.module) ?? []), item]);
  return [...groups.entries()];
}

function RolesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const canManage = usePermissions().has("iam.role.manage");
  useDocumentTitle(t("menu.roles"));
  const [panel, setPanel] = useState<{ role: Role | null } | null>(null);

  const roles = useQuery({
    queryKey: queryKeys.roles,
    queryFn: async () => (await api.GET("/roles")).data!.items,
  });
  const catalog = useQuery({
    queryKey: queryKeys.permissions,
    staleTime: Infinity,
    queryFn: async () => (await api.GET("/permissions")).data!.items as CatalogItem[],
  });
  const groups = useMemo(() => groupByModule(catalog.data ?? []), [catalog.data]);

  return (
    <>
      <PageHeader
        title={t("menu.roles")}
        description={t("roles.description")}
        breadcrumb={[{ label: t("menu.groups.settings") }, { label: t("menu.roles") }]}
        actions={
          canManage ? (
            <Button onClick={() => setPanel({ role: null })}>
              <Plus aria-hidden="true" />
              {t("roles.add")}
            </Button>
          ) : null
        }
      />
      {roles.error ? <Alert tone="danger">{errorMessage(roles.error)}</Alert> : null}
      {!roles.data || !catalog.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <RoleMatrix roles={roles.data} groups={groups} onOpenRole={(role) => setPanel({ role })} />
      )}

      <Drawer
        open={panel !== null}
        onOpenChange={(open) => !open && setPanel(null)}
        title={panel?.role ? panel.role.name : t("roles.add")}
        {...(panel?.role?.isSystem ? { description: t("roles.systemRoleHint") } : {})}
        className="max-w-2xl"
      >
        {panel ? (
          <RoleForm
            key={panel.role?.id ?? "new"}
            role={panel.role}
            groups={groups}
            readOnly={!canManage || (panel.role?.isSystem ?? false)}
            onCancel={() => setPanel(null)}
            onSaved={() => {
              setPanel(null);
              toast({ tone: "success", title: t("common.saved") });
            }}
          />
        ) : null}
      </Drawer>
    </>
  );
}

/** Roller × izinler matrisi: izinler modül bazında gruplanır; sütun başlığına tıklayınca rol açılır. */
function RoleMatrix({
  roles,
  groups,
  onOpenRole,
}: {
  roles: Role[];
  groups: ModuleGroup[];
  onOpenRole: (role: Role) => void;
}): ReactElement {
  const { t } = useTranslation();
  const permissionSets = useMemo(
    () => new Map(roles.map((role) => [role.id, new Set<string>(role.permissions)])),
    [roles],
  );
  return (
    <Card className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{t("roles.matrixCaption")}</caption>
        <thead>
          <tr className="border-b bg-muted/60">
            <th
              scope="col"
              className="sticky left-0 z-10 min-w-64 bg-muted px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              {t("roles.permission")}
            </th>
            {roles.map((role) => (
              <th key={role.id} scope="col" className="min-w-28 px-2 py-2 text-center align-bottom">
                <button
                  type="button"
                  onClick={() => onOpenRole(role)}
                  className={cn(
                    "inline-flex flex-col items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold hover:bg-muted",
                    !role.isActive && "text-muted-foreground line-through",
                  )}
                >
                  {role.isSystem ? (
                    <Lock className="size-3.5 text-muted-foreground" aria-label={t("roles.systemRole")} />
                  ) : null}
                  {role.name}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(([module, items]) => (
            <Fragment key={module}>
              <tr className="border-b bg-muted/30">
                <th
                  scope="colgroup"
                  colSpan={roles.length + 1}
                  className="sticky left-0 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide"
                >
                  {module}
                </th>
              </tr>
              {items.map((item) => (
                <tr key={item.code} className="border-b last:border-b-0">
                  <th scope="row" className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-normal">
                    <span className="flex items-center gap-2">
                      {item.description}
                      {item.stepUp ? (
                        <ShieldAlert
                          className="size-3.5 shrink-0 text-warning"
                          aria-label={t("roles.stepUpRequired")}
                        />
                      ) : null}
                    </span>
                  </th>
                  {roles.map((role) => (
                    <td key={role.id} className="px-2 py-2 text-center">
                      {permissionSets.get(role.id)?.has(item.code) ? (
                        <Check className="mx-auto size-4 text-success" aria-label={t("common.yes")} />
                      ) : (
                        <span className="text-muted-foreground" aria-label={t("common.no")}>
                          ·
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      <p className="flex items-center gap-2 border-t px-3 py-2 text-xs text-muted-foreground">
        <ShieldAlert className="size-3.5 text-warning" aria-hidden="true" />
        {t("roles.stepUpLegend")}
      </p>
    </Card>
  );
}

type RoleValues = z.input<typeof createRoleSchema>;

function RoleForm({
  role,
  groups,
  readOnly,
  onSaved,
  onCancel,
}: {
  role: Role | null;
  groups: ModuleGroup[];
  readOnly: boolean;
  onSaved: () => void;
  onCancel: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const withStepUp = useStepUp();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<RoleValues>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      name: role?.name ?? "",
      description: role?.description ?? "",
      permissions: role?.permissions ?? [],
    },
  });
  const { errors, isDirty, isSubmitting } = form.formState;
  useDraftGuard(isDirty && !isSubmitting);

  const save = async (body: Record<string, unknown>): Promise<void> => {
    setFormError(null);
    try {
      await withStepUp(() =>
        role
          ? api.PATCH("/roles/{id}", { params: { path: { id: role.id } }, body })
          : api.POST("/roles", { body: body as z.infer<typeof createRoleSchema> }),
      );
      // Rol izinleri değişince etkin kullanıcının kendi izinleri de değişmiş olabilir.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.roles }),
        queryClient.invalidateQueries({ queryKey: queryKeys.me }),
        queryClient.invalidateQueries({ queryKey: queryKeys.users }),
      ]);
      form.reset(form.getValues());
      onSaved();
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, ["name", "description", "permissions"]).length > 0 ||
        Object.keys(form.formState.errors).length === 0
      )
        setFormError(errorMessage(error));
    }
  };
  const submit = form.handleSubmit((values) => save({ ...values, description: values.description || null }));

  return (
    <form onSubmit={(event) => void submit(event)} className="grid gap-5" noValidate>
      {formError ? <Alert tone="danger">{formError}</Alert> : null}
      {role && !role.isActive ? <Alert tone="warning">{t("roles.inactiveHint")}</Alert> : null}
      <FormField label={t("roles.fields.name")} error={errors.name?.message} required>
        {(control) => <Input {...control} {...form.register("name")} disabled={readOnly} autoFocus={!role} />}
      </FormField>
      <FormField label={t("roles.fields.description")} error={errors.description?.message}>
        {(control) => (
          <Textarea {...control} {...form.register("description")} disabled={readOnly} rows={2} />
        )}
      </FormField>

      <Controller
        control={form.control}
        name="permissions"
        render={({ field }) => {
          const selected = new Set<Permission>(field.value);
          const setMany = (codes: Permission[], checked: boolean) => {
            const next = new Set(selected);
            codes.forEach((code) => (checked ? next.add(code) : next.delete(code)));
            field.onChange([...next]);
          };
          return (
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold">{t("roles.permissions")}</h2>
                <StatusBadge>{t("roles.selectedCount", { count: selected.size })}</StatusBadge>
              </div>
              {errors.permissions?.message ? (
                <p role="alert" className="text-xs font-medium text-destructive">
                  {errors.permissions.message}
                </p>
              ) : null}
              {groups.map(([module, items]) => {
                const codes = items.map((item) => item.code);
                const count = codes.filter((code) => selected.has(code)).length;
                return (
                  <fieldset key={module} className="rounded-md border">
                    <legend className="sr-only">{module}</legend>
                    <label className="flex items-center gap-3 border-b bg-muted/40 px-3 py-2 text-sm font-semibold">
                      <Checkbox
                        checked={count === codes.length}
                        ref={(element) => {
                          if (element) element.indeterminate = count > 0 && count < codes.length;
                        }}
                        disabled={readOnly}
                        onChange={(event) => setMany(codes, event.target.checked)}
                        aria-label={t("roles.selectAllIn", { module })}
                      />
                      <span aria-hidden="true">{module}</span>
                      <span className="ml-auto text-xs font-normal text-muted-foreground">
                        {count}/{codes.length}
                      </span>
                    </label>
                    <ul className="grid gap-1 p-3">
                      {items.map((item) => (
                        <li key={item.code}>
                          <label className="flex items-start gap-3 text-sm">
                            <Checkbox
                              className="mt-0.5"
                              checked={selected.has(item.code)}
                              disabled={readOnly}
                              onChange={(event) => setMany([item.code], event.target.checked)}
                            />
                            <span className="grid">
                              <span className="flex items-center gap-2">
                                {item.description}
                                {item.stepUp ? (
                                  <ShieldAlert
                                    className="size-3.5 text-warning"
                                    aria-label={t("roles.stepUpRequired")}
                                  />
                                ) : null}
                              </span>
                              <code className="text-xs text-muted-foreground">{item.code}</code>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                );
              })}
            </div>
          );
        }}
      />

      <div className="flex flex-wrap justify-between gap-2">
        {role && !role.isSystem && !readOnly ? (
          <Button
            variant="outline"
            onClick={() => void save({ isActive: !role.isActive })}
            disabled={isSubmitting}
          >
            {role.isActive ? t("common.deactivate") : t("common.activate")}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            {readOnly ? t("common.close") : t("common.cancel")}
          </Button>
          {readOnly ? null : (
            <Button type="submit" loading={isSubmitting}>
              {t("common.save")}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}
